import React from 'react';
import { SavedLocation } from '../TimeIn';
import StatusDisplay from './StatusDisplay';
import { FaCheckCircle, FaClock } from 'react-icons/fa';
import './LocationForm.css';

interface LocationFormProps {
  locationName: string;
  radius: number;
  isTimedIn: boolean;
  isWithinRadius: boolean;
  timeInTime: Date | null;
  lastAction: string;
  onLocationNameChange: (name: string) => void;
  onRadiusChange: (radius: number) => void;
  onTimeAction: (action: 'in' | 'out') => void;
  onSubmit: (e: React.FormEvent) => void;
  savedLocation: SavedLocation | null;
  savedLocations: SavedLocation[];
  showAddLocation: boolean;
  onToggleAddLocation: () => void;
  onSelectLocation: (locationId: string) => Promise<void>;
  hasAmTimeIn: boolean;
  hasAmTimeOut: boolean;
  hasPmTimeIn: boolean;
  hasPmTimeOut: boolean;
  isInAmWindow: boolean;
  isInPmWindow: boolean;
}

const LocationForm: React.FC<LocationFormProps> = ({
  locationName,
  radius,
  isTimedIn,
  isWithinRadius,
  timeInTime,
  lastAction,
  onLocationNameChange,
  onRadiusChange,
  onTimeAction,
  onSubmit,
  savedLocation,
  savedLocations,
  showAddLocation,
  onToggleAddLocation,
  onSelectLocation,
  hasAmTimeIn,
  hasAmTimeOut,
  hasPmTimeIn,
  hasPmTimeOut,
  isInAmWindow,
  isInPmWindow
}) => {
  // Filter locations by approval status (exclude rejected)
  const approvedLocations = savedLocations.filter(loc => loc.approval_status === 'approved');
  const pendingLocations = savedLocations.filter(loc => loc.approval_status === 'pending');

  return (
    <div className="location-form">
      <div className="saved-locations">
        <h3>Saved Locations</h3>
        
        {/* Approved Locations */}
        {approvedLocations.length > 0 && (
          <div className="location-section">
            <h4 className="location-section-title approved-title">Approved Locations</h4>
            <div className="location-buttons">
              {approvedLocations.map((location) => (
                <button
                  key={location.id}
                  type="button"
                  className={`location-btn ${location.is_active ? 'active' : ''}`}
                  onClick={() => onSelectLocation(location.id)}
                >
                  <span className="location-name">{location.name}</span>
                  <span className="approval-badge approved"><FaCheckCircle /> Approved</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Pending Locations */}
        {pendingLocations.length > 0 && (
          <div className="location-section">
            <h4 className="location-section-title pending-title">Pending Approval</h4>
            <div className="location-buttons">
              {pendingLocations.map((location) => (
                <button
                  key={location.id}
                  type="button"
                  className="location-btn not-approved"
                  disabled
                  title="This location is pending approval from OJT Coordinator"
                >
                  <span className="location-name">{location.name}</span>
                  <span className="approval-badge pending"><FaClock /> Pending</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {approvedLocations.length === 0 && pendingLocations.length === 0 && (
          <p className="no-locations-message">No saved locations yet. Add a new location below.</p>
        )}

        <button 
          type="button" 
          className="add-location-btn"
          onClick={onToggleAddLocation}
        >
          {showAddLocation ? '− Cancel' : '+ Request New Location'}
        </button>
      </div>

      {showAddLocation && (
        <form onSubmit={onSubmit}>
          <div className="input-group">
            <label htmlFor="location">Location Name</label>
            <input
              type="text"
              id="location"
              value={locationName}
              onChange={(e) => onLocationNameChange(e.target.value)}
              placeholder="e.g., Work, School"
              required
            />
        </div>

        <div className="input-group">
          <label htmlFor="radius">Radius (meters)</label>
          <input
            type="number"
            id="radius"
            value={radius}
            onChange={(e) => onRadiusChange(Math.max(5, Math.min(20, Number(e.target.value))))}
            min="5"
            max="20"
            required
          />
          <div className="radius-helper">
            Min: 5m, Max: 20m
          </div>
        </div>


        <button 
          type="submit" 
          className="save-btn"
        >
          Save New Location
        </button>
      </form>
      )}

      <StatusDisplay
        isTimedIn={isTimedIn}
        isWithinRadius={isWithinRadius}
        timeInTime={timeInTime}
        lastAction={lastAction}
        savedLocation={savedLocation}
        onTimeAction={onTimeAction}
        hasAmTimeIn={hasAmTimeIn}
        hasAmTimeOut={hasAmTimeOut}
        hasPmTimeIn={hasPmTimeIn}
        hasPmTimeOut={hasPmTimeOut}
        isInAmWindow={isInAmWindow}
        isInPmWindow={isInPmWindow}
      />


    </div>
  );
};

export default LocationForm;

